// src/lib/webauthn.js
// Helpers para el flujo de WebAuthn (Face ID / Touch ID / huella / Hello).
//
// La biometría real nunca sale del dispositivo: lo que viaja al servidor
// es una public key + firma del challenge. Cumple privacy-first.
//
// Bugfix B3.5: la versión inicial reportaba errores genéricos ("Cancelaste o
// se agotó el tiempo") cuando en realidad el flow fallaba por otras causas
// (rpID mismatch, plataforma sin soporte, navegador in-app, etc.). Ahora
// devolvemos el `name` + `message` + `cause` del error para diagnóstico.
// La auditoría de webauthn_challenges mostró 4 challenges generados con 0
// credentials guardados → el navegador nunca llegó a registrar realmente.

import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { supabase } from './supabase';

export function soportaBiometria() {
  return browserSupportsWebAuthn();
}

// Convierte cualquier excepción del browser en un mensaje claro y accionable
// para el usuario. Mantiene `e.name` para que el caller pueda decidir
// (e.g. NotAllowedError ≠ NotSupportedError).
function explicarErrorBrowser(e) {
  const name = e?.name || 'Error';
  const msg = e?.message || '';
  // Errores típicos de WebAuthn:
  switch (name) {
    case 'NotAllowedError':
      return 'Cancelaste el prompt o se agotó el tiempo. Intentá de nuevo y autoriza con tu cara, huella o PIN cuando aparezca el prompt del sistema.';
    case 'NotSupportedError':
      return 'Tu navegador o dispositivo no soporta este tipo de credencial. Prueba con otro navegador (Safari/Chrome última versión) o desactivá modo navegador privado.';
    case 'SecurityError':
      return `Error de seguridad: ${msg || 'el dominio no coincide con el registrado'}. Asegúrate de estar en https://ia-vera.com (no en una variante).`;
    case 'InvalidStateError':
      return 'Este dispositivo ya tiene una credencial registrada para esta cuenta. Si quieres volver a registrarlo, contacta a soporte para limpiar el credential anterior.';
    case 'AbortError':
      return 'El registro fue cancelado por el navegador. Intenta de nuevo.';
    case 'ConstraintError':
      return `El authenticator rechazó la solicitud: ${msg}. Tu dispositivo podría no soportar el tipo de credencial pedido.`;
    default:
      return msg ? `${name}: ${msg}` : `Error inesperado (${name}).`;
  }
}

/**
 * Registra un nuevo authenticator para el usuario logueado.
 * Requiere que `supabase.auth.getSession()` devuelva una sesión activa.
 *
 * @param {string} [deviceName] Nombre opcional ("MacBook trabajo", etc.)
 * @returns {Promise<{ ok: boolean, device_name?: string, error?: string }>}
 */
export async function registrarBiometria(deviceName) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { ok: false, error: 'Tienes que estar logueado para activar biometría.' };

  // 1. Pedir options al server (challenge)
  const optsRes = await fetch('/api/auth', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ op: 'register-options' }),
  });
  if (!optsRes.ok) {
    const err = await optsRes.json().catch(() => ({}));
    return { ok: false, error: err.error || 'No se pudo iniciar el registro.' };
  }
  const { options } = await optsRes.json();

  // Validación defensiva: si el server devuelve algo sin challenge o sin
  // user.id, la lib del browser explotaría con un TypeError críptico
  // ('undefined is not an object evaluating e.replace'). Mejor cortar aquí
  // con mensaje claro Y dump del shape para debug.
  if (!options || typeof options !== 'object') {
    console.error('[webauthn] server devolvió options inválido:', options);
    return { ok: false, error: 'El servidor no devolvió las opciones de registro. Prueba de nuevo en un momento.' };
  }
  if (!options.challenge || !options.user?.id) {
    console.error('[webauthn] options con campos faltantes:', options);
    return {
      ok: false,
      error: `Las opciones del servidor están incompletas (falta ${!options.challenge ? 'challenge' : 'user.id'}). Reporta este caso a soporte con la fecha y hora.`,
      error_raw: JSON.stringify({ keys: Object.keys(options), userKeys: options.user ? Object.keys(options.user) : null }),
    };
  }
  // Debug log comentado en prod (Sprint 15.28 cleanup) — descomentar si hay
  // que diagnosticar problemas de WebAuthn registration:
  // console.log('[webauthn] options OK, shape:', {
  //   challenge: options.challenge?.slice?.(0, 12) + '...',
  //   rpName: options.rp?.name,
  //   rpID: options.rp?.id,
  //   userId: options.user?.id?.slice?.(0, 12) + '...',
  //   excludeCredentials: options.excludeCredentials?.length ?? 0,
  // });

  // 2. Pedir al browser/SO que genere el credential (esto dispara Face ID,
  //    Touch ID, huella, etc.). Si falla, propagamos el error específico.
  //
  // Bugfix B3.5: la firma de @simplewebauthn/browser v10 es
  // `startRegistration(optionsJSON)` — recibe el objeto directo, NO
  // envuelto en { optionsJSON }. Pasar el wrapper hacía que la lib viera
  // `optionsJSON.challenge = undefined` y explotara con
  // "TypeError: undefined is not an object (evaluating 'e.replace')"
  // dentro de su helper base64URLStringToBuffer.
  let credential;
  try {
    credential = await startRegistration(options);
  } catch (e) {
    // Log a consola para debug (Pablo puede ver `e.name` y `e.message`).
    console.error('[webauthn] startRegistration falló:', e?.name, e?.message, e);
    return {
      ok: false,
      error: explicarErrorBrowser(e),
      error_name: e?.name,
      error_raw: e?.message,
    };
  }

  // 3. Verificar y persistir
  const verifyRes = await fetch('/api/auth', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ op: 'register-verify', credential, deviceName }),
  });
  if (!verifyRes.ok) {
    const err = await verifyRes.json().catch(() => ({}));
    console.error('[webauthn] register-verify falló:', verifyRes.status, err);
    const detail = err.detail ? ` (${err.detail})` : '';
    return { ok: false, error: (err.error || 'No se pudo verificar el registro') + detail };
  }
  const data = await verifyRes.json();
  return { ok: true, device_name: data.device_name };
}

/**
 * Login biométrico para un email dado. Devuelve la sesión Supabase si
 * todo OK; el caller debe llamar setSession después.
 *
 * @param {string} email
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function loginConBiometria(email) {
  if (!email) return { ok: false, error: 'Falta email' };

  // 1. Pedir auth options para ese email
  const optsRes = await fetch('/api/auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op: 'auth-options', email }),
  });
  if (!optsRes.ok) {
    const err = await optsRes.json().catch(() => ({}));
    return { ok: false, error: err.error || 'No se pudo iniciar el login.' };
  }
  const { options, has_credentials } = await optsRes.json();
  if (!has_credentials) {
    return { ok: false, error: 'Este email no tiene biometría activada. Entrá con tu contraseña.' };
  }

  // Validación defensiva: si options viene mal-formado, la lib del browser
  // explota con TypeError críptico. Cortamos antes con mensaje claro.
  if (!options || typeof options !== 'object') {
    console.error('[webauthn] auth-options devolvió options inválido:', options);
    return { ok: false, error: 'El servidor no devolvió las opciones de login. Prueba de nuevo.' };
  }
  if (!options.challenge) {
    console.error('[webauthn] auth-options sin challenge:', options);
    return {
      ok: false,
      error: 'Las opciones del servidor no tienen challenge. Reporta a soporte.',
      error_raw: JSON.stringify({ keys: Object.keys(options) }),
    };
  }
  // Si allowCredentials está, debe tener id en cada uno (sino startAuthentication explota).
  if (Array.isArray(options.allowCredentials)) {
    const malos = options.allowCredentials.filter(c => !c?.id);
    if (malos.length > 0) {
      console.error('[webauthn] allowCredentials con items sin id:', options.allowCredentials);
      return {
        ok: false,
        error: 'Hay credenciales registradas pero el servidor las devolvió incompletas. Reporta a soporte con la fecha y hora.',
        error_raw: JSON.stringify({ totalCredentials: options.allowCredentials.length, primerItem: options.allowCredentials[0] }),
      };
    }
  }
  // Debug log comentado en prod (Sprint 15.28 cleanup) — descomentar si hay
  // que diagnosticar problemas de WebAuthn login:
  // console.log('[webauthn] login options OK, shape:', {
  //   challenge: options.challenge?.slice?.(0, 12) + '...',
  //   rpID: options.rpId,
  //   allowCredentials: options.allowCredentials?.length ?? 0,
  //   primerCredId: options.allowCredentials?.[0]?.id?.slice?.(0, 12) + '...',
  //   userVerification: options.userVerification,
  // });

  // 2. Browser/SO valida con biométrico (Face ID, Touch ID, etc.)
  // Mismo bugfix que registrarBiometria: pasar options directamente,
  // sin envolver en { optionsJSON }.
  let credential;
  try {
    credential = await startAuthentication(options);
  } catch (e) {
    console.error('[webauthn] startAuthentication falló:', e?.name, e?.message, e);
    return {
      ok: false,
      error: explicarErrorBrowser(e),
      error_name: e?.name,
      error_raw: e?.message,
    };
  }

  // 3. Verificar y crear sesión Supabase
  const verifyRes = await fetch('/api/auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op: 'auth-verify', email, credential }),
  });
  if (!verifyRes.ok) {
    const err = await verifyRes.json().catch(() => ({}));
    return { ok: false, error: err.error || 'No se pudo verificar la firma.' };
  }
  const { token_hash } = await verifyRes.json();

  // 4. Consumir el token_hash para crear sesión Supabase legítima
  const { error: otpErr } = await supabase.auth.verifyOtp({
    token_hash,
    type: 'magiclink',
  });
  if (otpErr) {
    return { ok: false, error: otpErr.message || 'No se pudo crear la sesión.' };
  }
  return { ok: true };
}
